// src/components/roads/page/QuickReferencePanel.js
// Extracted from RoadWorksPage v4.7 — collapsible onboarding guide.
// --- UPDATE: v5.0 — memoised, keyboard accessible header (Enter/Space), AI/ML workflow steps added
import React, { memo, useCallback, useMemo } from 'react';
import {
    Box, Flex, HStack, Icon, Text, SimpleGrid, Collapse, Alert, AlertIcon,
    AlertTitle, AlertDescription, useColorModeValue,
} from '@chakra-ui/react';
import { FiInfo } from 'react-icons/fi';
import {
    MdAddRoad, MdAssessment, MdSchedule, MdReportProblem, MdBuild, MdAnalytics, MdExpandMore,
} from 'react-icons/md';
import { GlassCard, MotionBox } from './shared';
// --- UPDATE
import { AiForecastIcon, AiClusterIcon, AiChip } from './AiMlIcons';
// --- END UPDATE

const QuickReferencePanel = ({ showQuickRef, setShowQuickRef }) => {
    const borderColor = useColorModeValue('gray.200','gray.700');
    const headingColor= useColorModeValue('gray.800','white');
    const mutedColor  = useColorModeValue('gray.500','gray.400');
    const headerHover = useColorModeValue('gray.50','gray.750');
    const iconBg      = useColorModeValue('blue.50','blue.900');
    const alertBg     = useColorModeValue('blue.50','blue.900');
    const itemHoverBg = useColorModeValue('gray.50','gray.700');

    const steps = useMemo(() => [
        { n:1, icon:MdAddRoad,    color:'blue',   text:'Add road segments to your inventory with location coordinates and metadata.' },
        { n:2, icon:MdAssessment, color:'green',  text:'Record condition assessments — system auto-derives PCI rating from your score.' },
        { n:3, icon:MdSchedule,   color:'cyan',   text:'Schedule routine inspections to maintain proactive monitoring cadence.' },
        { n:4, icon:MdReportProblem,color:'orange',text:'Enable citizen issue reporting — high-severity reports auto-create work orders.' },
        { n:5, icon:MdBuild,      color:'purple', text:'Generate work orders for paving, pothole repair, drainage, or line marking.' },
        { n:6, icon:MdAnalytics,  color:'teal',   text:'Monitor network analytics to identify trends and budget priorities.' },
        // --- UPDATE: AI/ML workflow steps
        { n:7, icon:AiForecastIcon, color:'pink', ai:'forecast', text:'Run "Forecast Risk" to refresh Random Survival Forest RUL scores across the network.' },
        { n:8, icon:AiClusterIcon,  color:'red',  ai:'ml',       text:'On the GIS tab, cluster dashcam anomalies into actionable AI work orders.' },
        // --- END UPDATE
    ], []);

    // --- UPDATE: stable toggle + keyboard support
    const toggle = useCallback(() => setShowQuickRef(!showQuickRef), [showQuickRef, setShowQuickRef]);
    const onKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
    }, [toggle]);
    // --- END UPDATE

    return (
        <GlassCard hoverLift={false}>
            <Flex justify="space-between" align="center" p={4}
                borderBottomWidth={showQuickRef ? '1px' : '0'} borderColor={borderColor}
                cursor="pointer" onClick={toggle}
                // --- UPDATE
                role="button" tabIndex={0} aria-expanded={showQuickRef} onKeyDown={onKeyDown}
                // --- END UPDATE
                _hover={{ bg:headerHover }} transition="all 0.2s" borderRadius="2xl">
                <HStack spacing={3}>
                    <MotionBox
                        animate={{ rotate: showQuickRef ? 360 : 0 }}
                        transition={{ duration:0.4 }}
                    >
                        <Box bg={iconBg} p={2} borderRadius="lg">
                            <Icon as={FiInfo} color="blue.500" boxSize={5} />
                        </Box>
                    </MotionBox>
                    <Box>
                        <Text fontWeight="700" fontSize="sm" color={headingColor}>Quick Reference Guide</Text>
                        <Text fontSize="xs" color={mutedColor}>FlowSense MOW+ road infrastructure setup</Text>
                    </Box>
                </HStack>
                <MotionBox animate={{ rotate: showQuickRef ? 180 : 0 }} transition={{ duration:0.2 }}>
                    <Icon as={MdExpandMore} boxSize={5} color={mutedColor} />
                </MotionBox>
            </Flex>

            <Collapse in={showQuickRef} animateOpacity>
                <Box p={5}>
                    <SimpleGrid columns={{ base:1, md:2 }} spacing={3} mb={4}>
                        {steps.map(({ n, icon, color, text, ai }) => (
                            <MotionBox key={n}
                                initial={{ opacity:0, x:-15 }} animate={{ opacity:1, x:0 }}
                                transition={{ delay: n * 0.06 }}
                                whileHover={{ x:4, boxShadow:'sm' }}
                            >
                                <HStack p={3} borderRadius="xl" _hover={{ bg:itemHoverBg }}
                                    transition="all 0.2s" spacing={3}>
                                    <Flex w="28px" h="28px" bg={`${color}.500`} color="white"
                                        borderRadius="lg" justify="center" align="center"
                                        fontWeight="800" fontSize="xs" flexShrink={0}
                                        boxShadow={`0 4px 12px var(--chakra-colors-${color}-200)`}>
                                        {n}
                                    </Flex>
                                    <Icon as={icon} color={`${color}.500`} boxSize={4} flexShrink={0} />
                                    <Text fontSize="sm" color={mutedColor} flex={1}>{text}</Text>
                                    {/* --- UPDATE */}
                                    {ai && <AiChip kind={ai} size="xs" />}
                                    {/* --- END UPDATE */}
                                </HStack>
                            </MotionBox>
                        ))}
                    </SimpleGrid>

                    <Alert status="info" variant="subtle" borderRadius="xl" bg={alertBg}>
                        <AlertIcon />
                        <Box>
                            <AlertTitle fontSize="sm">Pro Tip — PCI Auto-Flagging</AlertTitle>
                            <AlertDescription fontSize="xs">
                                When you record a condition assessment with a score below 55,
                                the system automatically flags the road as "Maintenance Required"
                                and surfaces it in the AI insights panel for your action.
                            </AlertDescription>
                        </Box>
                    </Alert>
                </Box>
            </Collapse>
        </GlassCard>
    );
};

export default memo(QuickReferencePanel);
